import { normaliseer } from './zoekMatch';

/**
 * Knoppen en links op het scherm vindbaar maken. Het zoekvenster leest bij
 * openen het huidige scherm uit, en onthoudt per gebruiker welke knoppen op
 * eerder bezochte pagina's staan, zodat ook die via zoeken uitvoerbaar zijn.
 */

const KANDIDATEN =
  'button, a[href], [role="button"], [role="tab"], [role="menuitem"], [role="switch"], input[type="submit"], input[type="button"], summary';

/**
 * Niet scannen: het zoekvenster en de zwevende knoppen zelf, en de bovenbalk
 * in Layout (die staat al compleet als pagina's in de zoekindex).
 */
const NEGEER = '[data-zoek-negeer]';

const MAX_ONTHOUDEN = 600;

export interface PaginaKnop {
  label: string;
  omschrijving?: string;
  context?: string;
  element: HTMLElement;
}

export interface OnthoudenKnop {
  label: string;
  context?: string;
  href: string;
  paginaTitel: string;
}

/** Eerste regel is het label; een tweede regel (menu-items met uitleg) is de omschrijving. */
const tekstVan = (
  el: HTMLElement
): { label: string; omschrijving?: string } => {
  const aria = el.getAttribute('aria-label');
  if (aria) return { label: aria.trim() };
  if (el instanceof HTMLInputElement) return { label: el.value.trim() };
  const regels = (el.innerText || el.getAttribute('title') || '')
    .split('\n')
    .map(r => r.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  const omschrijving = regels.slice(1).join(' ');
  return {
    label: regels[0] ?? '',
    omschrijving:
      omschrijving && omschrijving.length <= 120 ? omschrijving : undefined,
  };
};

const isZichtbaar = (el: HTMLElement): boolean => {
  if (el.closest('[aria-hidden="true"], [hidden], [inert]')) return false;
  const r = el.getBoundingClientRect();
  if (r.width < 2 || r.height < 2) return false;
  const s = window.getComputedStyle(el);
  return (
    s.visibility !== 'hidden' &&
    s.display !== 'none' &&
    s.pointerEvents !== 'none'
  );
};

const isUitgeschakeld = (el: HTMLElement): boolean =>
  (el as HTMLButtonElement).disabled === true ||
  el.getAttribute('aria-disabled') === 'true';

/** Bruikbaar label: geen losse cijfers, tellers of extreem lange teksten. */
const isBruikbaarLabel = (label: string): boolean =>
  label.length >= 2 && label.length <= 70 && /[a-zA-ZÀ-ɏ]{2}/.test(label);

/** Laatste kop die vóór de knop staat in een omliggend blok, als context ("Facturen"). */
const contextVan = (el: HTMLElement): string | undefined => {
  const grens =
    el.closest('[role="dialog"]') ??
    document.querySelector('main') ??
    document.body;
  let node: HTMLElement | null = el.parentElement;
  for (
    let diepte = 0;
    node && diepte < 6;
    diepte++, node = node.parentElement
  ) {
    const koppen = Array.from(
      node.querySelectorAll<HTMLElement>('h1, h2, h3, h4, [role="heading"]')
    ).filter(
      kop =>
        !kop.contains(el) &&
        !kop.closest(NEGEER) &&
        kop.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING
    );
    const kop = koppen[koppen.length - 1];
    if (kop) {
      const tekst = kop.innerText.replace(/\s+/g, ' ').trim();
      if (tekst && tekst.length <= 60) return tekst;
    }
    if (node === grens) break;
  }
  return undefined;
};

/** Alle zichtbare, klikbare onderdelen van het huidige scherm (zonder dubbelen). */
export const scanPagina = (): PaginaKnop[] => {
  const gezien = new Set<string>();
  const knoppen: PaginaKnop[] = [];
  document.querySelectorAll<HTMLElement>(KANDIDATEN).forEach(el => {
    if (el.closest(NEGEER) || isUitgeschakeld(el) || !isZichtbaar(el)) return;
    const { label, omschrijving } = tekstVan(el);
    if (!isBruikbaarLabel(label)) return;
    const context = contextVan(el);
    const sleutel = normaliseer(label) + '|' + normaliseer(context ?? '');
    if (gezien.has(sleutel)) return;
    gezien.add(sleutel);
    knoppen.push({
      label,
      omschrijving,
      context:
        context && normaliseer(context) !== normaliseer(label)
          ? context
          : undefined,
      element: el,
    });
  });
  return knoppen;
};

// ── Onthouden knoppen (per gebruiker, alleen in deze browser) ──────────────

const opslagSleutel = (gebruiker: string) =>
  `belagom.zoeken.knoppen.${gebruiker}`;

export const leesOnthouden = (gebruiker: string): OnthoudenKnop[] => {
  try {
    const raw = localStorage.getItem(opslagSleutel(gebruiker));
    const data = raw ? JSON.parse(raw) : [];
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
};

/**
 * Alleen echte knoppen onthouden: links zijn navigatie (staan al als pagina in
 * de index of wijzen naar één specifiek record), en menu's en dialogen
 * verschijnen niet vanzelf na het openen van de pagina.
 */
const isOnthoudbaar = (el: HTMLElement): boolean =>
  !(el instanceof HTMLAnchorElement) &&
  !el.closest('nav, aside, [role="dialog"], [role="menu"]');

export const wisOnthouden = (gebruiker: string) => {
  try {
    localStorage.removeItem(opslagSleutel(gebruiker));
  } catch {
    // Geen opslag: niets te wissen.
  }
};

export const onthoudPagina = (
  gebruiker: string,
  href: string,
  paginaTitel: string,
  knoppen: PaginaKnop[]
) => {
  try {
    const nieuw = knoppen
      .filter(k => isOnthoudbaar(k.element))
      .map<OnthoudenKnop>(k => ({
        label: k.label,
        context: k.context,
        href,
        paginaTitel,
      }));
    // De knoppen van deze pagina vervangen de vorige stand; nieuwste pagina's vooraan.
    const rest = leesOnthouden(gebruiker).filter(k => k.href !== href);
    localStorage.setItem(
      opslagSleutel(gebruiker),
      JSON.stringify([...nieuw, ...rest].slice(0, MAX_ONTHOUDEN))
    );
  } catch {
    // Opslag vol of geblokkeerd: zoeken werkt dan alleen op het huidige scherm.
  }
};

// ── Uitvoeren ───────────────────────────────────────────────────────────────

/** Woorden waarbij "Doe het" niet blind klikt maar eerst bevestiging vraagt. */
const RISICOVOL =
  /verwijder|wissen|delete|remove|uitlog|log ?out|afwijz|weiger|reject|intrek|beeindig|opzeg|annuleer|cancel|blokkeer|blacklist|archiveer/;

export const isRisicovol = (label: string) =>
  RISICOVOL.test(normaliseer(label));

/** Kort goudkleurig kader om de knop, zodat de gebruiker ziet waar hij staat. */
export const markeer = (el: HTMLElement) => {
  el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  const oud = {
    outline: el.style.outline,
    offset: el.style.outlineOffset,
    shadow: el.style.boxShadow,
    transition: el.style.transition,
  };
  el.style.transition = 'outline-color 0.3s, box-shadow 0.3s';
  el.style.outline = '3px solid #E5A425';
  el.style.outlineOffset = '3px';
  el.style.boxShadow = '0 0 0 8px rgba(229, 164, 37, 0.25)';
  window.setTimeout(() => {
    el.style.outline = oud.outline;
    el.style.outlineOffset = oud.offset;
    el.style.boxShadow = oud.shadow;
    el.style.transition = oud.transition;
  }, 2400);
};

export const voerUit = (el: HTMLElement) => {
  markeer(el);
  if (el instanceof HTMLInputElement && !['submit', 'button'].includes(el.type))
    el.focus();
  else el.click();
};

/** Zoek een knop op label (eventueel binnen een kop/context) in het huidige scherm. */
export const vindKnop = (
  label: string,
  context?: string
): HTMLElement | null => {
  const doel = normaliseer(label);
  const knoppen = scanPagina();
  const exact = knoppen.filter(k => normaliseer(k.label) === doel);
  const metContext = context
    ? exact.find(k => normaliseer(k.context ?? '') === normaliseer(context))
    : undefined;
  return (
    metContext?.element ??
    exact[0]?.element ??
    knoppen.find(k => normaliseer(k.label).startsWith(doel))?.element ??
    knoppen.find(k => normaliseer(k.label).includes(doel))?.element ??
    null
  );
};

/** Wacht tot de knop na navigatie verschijnt (pagina's laden data asynchroon). */
export const wachtOpKnop = (
  label: string,
  context?: string,
  maxMs = 6000
): Promise<HTMLElement | null> =>
  new Promise(resolve => {
    const start = Date.now();
    const poging = () => {
      const el = vindKnop(label, context);
      if (el) return resolve(el);
      if (Date.now() - start > maxMs) return resolve(null);
      window.setTimeout(poging, 200);
    };
    window.setTimeout(poging, 150);
  });
